import { Node, mergeAttributes } from "@tiptap/core";
import {
  CommandProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";
import { Typography } from "antd-v5";
import axios from "axios";
import { isString, isUndefined } from "lodash";
import { Plugin, PluginKey } from "prosemirror-state";
import React, { MouseEvent, useEffect, useRef, useState } from "react";
import FindingAPI from "restAPI/FindingAPI";
import { removeNonPrintableUnicodeCharacters } from "utils/fileUtils";

import LoadingIcon from "ac-components/ac-icons/LoadingIcon";

import "./ImageExtension.less";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    image: {
      insertImageAttachment: (file: File) => ReturnType;
    };
  }
}

const ImageExtension = Node.create({
  name: "image",
  group: "block",
  atom: true,
  addProseMirrorPlugins() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    return [
      new Plugin({
        key: new PluginKey("eventHandler"),
        props: {
          handlePaste(_view, event) {
            // this is to prevent if a file is copied from a computer and pasted, then it pastes the file as well as its thumbnail
            // if an image is copied from a doc file along with other texts then it won't be a file
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            const file = getFilesFromClipboardEvent(event);
            if (!file || file.type.indexOf("image") === -1) {
              return false;
            }
            self.editor.chain().focus().insertImageAttachment(file).run();
            self.editor.commands.setTextSelection(
              self.editor.state.selection.$from.pos + 2,
            );

            // Return true to signal paste was fully handled
            // This prevents TipTap from also parsing the HTML <img> tag from clipboard,
            // which was causing duplicate images when copying from web
            return true;
          },
        },
      }),
    ];
  },
  // @ts-expect-error TODO fix type
  addCommands() {
    return {
      insertImageAttachment:
        (file: File) =>
        ({ state, dispatch }: CommandProps) => {
          // Create the file-attachment node
          const fileAttachmentNode = this.type.create({ src: file });

          // Insert the file attachment node
          const transaction = state.tr.replaceSelectionWith(fileAttachmentNode);

          // Add a line break after the file-attachment
          const lineBreakNode = state.schema.nodes.paragraph.create();
          const newTransaction = transaction.insert(
            transaction.selection.$to.pos,
            lineBreakNode,
          );

          // Dispatch the transaction
          dispatch!(newTransaction);
        },
    };
  },
  addAttributes() {
    return {
      src: {},
      width: {
        default: "400px",
        parseHTML: (element: HTMLElement) => {
          const width = element.getAttribute("width");
          if (width) return width;
          // Fallback: extract width from src URL query params
          // (set by checkForImageDimensions during HTML→markdown save)
          const src = element.getAttribute("src");
          if (src) {
            try {
              const url = new URL(src, window.location.origin);
              return url.searchParams.get("width");
            } catch {
              // ignore invalid URLs
            }
          }
          return null;
        },
      },
      height: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const height = element.getAttribute("height");
          if (height) return height;
          const src = element.getAttribute("src");
          if (src) {
            try {
              const url = new URL(src, window.location.origin);
              return url.searchParams.get("height");
            } catch {
              // ignore invalid URLs
            }
          }
          return null;
        },
      },
      alt: {},
    };
  },
  parseHTML() {
    return [
      {
        tag: "img",
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const attributes = { ...HTMLAttributes };
    const { src } = attributes;
    if (!isString(src)) {
      delete attributes.src;
    }
    return ["img", mergeAttributes(attributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
});

export default ImageExtension;

// Exported for testing
export const ImageNodeView = (props: any) => {
  const imageRef = useRef<HTMLImageElement | null>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState(false);

  const { src, width } = props.node.attrs;

  useEffect(() => {
    const click = () => {
      setFocus(false);
    };
    window.addEventListener("click", click);
    return () => window.removeEventListener("click", click);
  }, []);

  useEffect(() => {
    if (src instanceof File) {
      const file = src;
      const upload = async () => {
        const fileName = removeNonPrintableUnicodeCharacters(file.name);
        const { id, signedUrl } = await FindingAPI.uploadManualAttachment({
          fileName,
        }).promise;
        await axios({ method: "put", url: signedUrl, data: file });
        const params = new URLSearchParams({
          attachmentId: id,
          filename: fileName,
        });
        return `/user/tools/generic/CUSTOM/attachment?${params.toString()}`;
      };
      setLoading(true);
      upload()
        .then((src) => {
          props.updateAttributes({
            src,
            alt: file.name,
          });
        })
        .catch(() => {
          props.deleteNode();
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // to avoid setting updateAttributes to deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const isEditorDisabled = !props.editor.options.editable;

  let startX: number, startWidth: number, index: number;

  const mousemove = (e: WindowEventMap["mousemove"]) => {
    if (!startX || !startWidth || isUndefined(index) || !imageRef.current) {
      return;
    }
    const imageElement = imageRef.current;
    const deltaX = index % 2 === 0 ? -(e.clientX - startX) : e.clientX - startX;

    const newWidth = startWidth + deltaX;

    imageElement.width = Math.min(newWidth, 600);

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      props.updateAttributes({
        width: Math.min(newWidth, 600),
      });
    }, 500);
  };
  const mouseup = () => {
    window.removeEventListener("mousemove", mousemove);
    window.removeEventListener("mouseup", mouseup);
  };

  const mousedown = (e: MouseEvent<HTMLDivElement>) => {
    startX = e.pageX;
    startWidth = imageRef.current?.width ?? 0;
    index = +e.currentTarget.dataset.index!;
    window.addEventListener("mousemove", mousemove);
    window.addEventListener("mouseup", mouseup);
  };

  const image = (
    <img
      ref={(i) => (imageRef.current = i)}
      src={src}
      alt={src}
      width={width}
    />
  );

  return (
    <NodeViewWrapper className="resizable-image">
      <span
        className="tiptap-resizable-image-wrapper"
        onClick={(e) => {
          if (isEditorDisabled) {
            return;
          }
          setFocus(true);
          e.stopPropagation();
        }}
      >
        {loading || !isString(src) ? (
          <LoadingIcon />
        ) : (
          <>
            {isEditorDisabled ? (
              <Typography.Link href={src} target="_blank">
                {image}
              </Typography.Link>
            ) : (
              image
            )}
            {focus ? (
              <>
                <div
                  className="tiptap-resizable-image-handle handle-1"
                  onMouseDown={mousedown}
                  data-index={0}
                />
                <div
                  className="tiptap-resizable-image-handle handle-2"
                  onMouseDown={mousedown}
                  data-index={1}
                />
                <div
                  className="tiptap-resizable-image-handle handle-3"
                  onMouseDown={mousedown}
                  data-index={2}
                />
                <div
                  className="tiptap-resizable-image-handle handle-4"
                  onMouseDown={mousedown}
                  data-index={3}
                />
              </>
            ) : null}
          </>
        )}
      </span>
    </NodeViewWrapper>
  );
};

function getFilesFromClipboardEvent(e: ClipboardEvent): File | undefined {
  const items = e.clipboardData?.items;
  const files: File[] = [];
  if (items) {
    for (let i = 0; i < items.length; i++) {
      const file = items[i].getAsFile();
      if (!file) continue;
      files.push(file);
    }
  }

  return files[0] || undefined;
}
